import React from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { usePortalContext } from '../context/PortalContext';
import ScopeTreeView, { type ScopeTreeEntry, type ScopeEntryDirtyState } from '../components/scopes/ScopeTreeView';
import ScopeEntryDetail from '../components/scopes/ScopeEntryDetail';
import ScopeModeBadge from '../components/scopes/ScopeModeBadge';
import ScopeDriftBanner from '../components/scopes/ScopeDriftBanner';
import ScopesEmptyState from '../components/scopes/ScopesEmptyState';
import ScopePublishDiffModal from '../components/scopes/ScopePublishDiffModal';
import ScopeStakeholderList, {
  type EditableStakeholder,
  type ScopeStakeholderDirtyState
} from '../components/scopes/ScopeStakeholderList';
import {
  acquireGraphFeatureTokenInteractively,
  acquireGraphFeatureTokenSilently
} from '../services/graph/interactiveGraphFeatureClient';

type ScopeMode = 'local' | 'mixed' | 'portal_enforced';
type ScopeSyncStatus = 'unknown' | 'aligned' | 'drifted' | 'needs_review' | 'error';
type ScopeSource = 'portal' | 'spfx_export' | 'm365_graph_structure';

type M365GraphImportState = {
  available: boolean;
  active: boolean;
  clientId?: string;
  tokenHeaderName: string;
  permissions: string[];
  permissionMayRequireItApproval: boolean;
};

type ScopeSetting = {
  tenantId: string;
  scopeMode: ScopeMode;
  tenantDefaultScopeKey: string | null;
  allowMixedMode: boolean;
  updatedAtUtc: string;
};

type ScopeEntry = {
  scopeEntryKey: string;
  parentScopeEntryKey: string | null;
  title: string;
  path: string | null;
  depth: number;
  sortOrder: number;
  isActive: boolean;
};

type ScopeStakeholderBinding = {
  scopeEntryKey: string | null;
  stakeholderType: 'portal_contact' | 'entra_group' | 'sharepoint_group' | 'email';
  stakeholderRef: string;
  displayName: string | null;
  bindingMode: 'audit_stakeholder_suggestion' | 'notification_default' | 'assignment_default';
  inheritsToChildren: boolean;
};

type ScopeSet = {
  scopeSetId: string;
  scopeKey: string;
  name: string;
  description: string | null;
  source: ScopeSource;
  status: 'draft' | 'published' | 'retired';
  version: number;
  entries: ScopeEntry[];
  stakeholders?: ScopeStakeholderBinding[];
  publications?: Array<{
    publicationId: string;
    publicationLevel: 'tenant' | 'instance' | 'template';
    instanceId: string | null;
    templateKey: string | null;
    isDefault: boolean;
    status: 'active' | 'retired';
  }>;
};

type ScopeImportRecord = {
  scopeImportId: string;
  sourceInstanceId: string | null;
  importSource: ScopeSource;
  proposedName: string;
  description: string | null;
  status: 'draft' | 'published' | 'discarded';
  createdAtUtc: string;
  createdBy: string | null;
  publishedScopeSetId: string | null;
  payload?: {
    entries?: unknown[];
    stakeholders?: unknown[];
    metadata?: {
      truncated?: boolean;
      userLimit?: number;
      collisions?: Array<{ originalKey: string; resolvedKey: string; title?: string }>;
      revisionOfScopeSetId?: string;
      revisionOfScopeKey?: string;
      revisionOfVersion?: number;
    };
  };
};

type ScopeImportEntry = {
  localId?: string;
  scopeKey?: string;
  parentLocalId?: string | null;
  parentScopeKey?: string | null;
  title: string;
  path?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
};

type ScopeImportStakeholder = {
  scopeKey?: string | null;
  localId?: string | null;
  stakeholderType: 'portal_contact' | 'entra_group' | 'sharepoint_group' | 'email';
  stakeholderRef: string;
  displayName?: string | null;
  bindingMode?: 'audit_stakeholder_suggestion' | 'notification_default' | 'assignment_default';
  inheritsToChildren?: boolean;
};

type ScopeImportPayload = {
  entries: ScopeImportEntry[];
  stakeholders?: ScopeImportStakeholder[];
  metadata?: Record<string, unknown>;
};

type InstanceScopeStatus = {
  instanceId: string;
  scopeMode: ScopeMode;
  appliedScopeSetId: string | null;
  appliedVersion: number | null;
  syncStatus: ScopeSyncStatus;
  lastReportedAtUtc: string;
  lastSyncAtUtc: string | null;
  lastError: string | null;
};

type PortalInstance = {
  instanceId: string;
  siteUrl?: string | null;
  pageUrl?: string | null;
  webPartVersion?: string | null;
  status?: 'active' | 'recent' | 'stale' | 'deregistered' | string;
  lastSeenAt?: string | null;
};

type ScopeWhereUsedListCount = {
  count: number;
  scannedFields: string[];
  failedFields: Array<{ fieldName: string; error: string }>;
};

type ScopeWhereUsedResult = {
  tenantId: string;
  instanceId: string;
  siteUrl: string;
  scopeEntryKey: string;
  fetchedAt: string;
  total: number;
  partial: boolean;
  counts: {
    audits: ScopeWhereUsedListCount;
    findings: ScopeWhereUsedListCount;
    actions: ScopeWhereUsedListCount;
  };
};

type InstanceBindingConfirmation = {
  scopeSetId: string;
  instanceId: string;
  scopeLabel: string;
  instanceLabel: string;
  previousScopeLabel: string | null;
};

type ScopePublicationEvent = {
  eventId: string;
  publicationId: string | null;
  previousPublicationId: string | null;
  scopeSetId: string;
  previousScopeSetId: string | null;
  version: number;
  previousVersion: number | null;
  publicationLevel: 'tenant' | 'instance' | 'template';
  instanceId: string | null;
  templateKey: string | null;
  isDefault: boolean;
  eventType: 'published' | 'changed';
  changedAtUtc: string;
  changedBy: string | null;
};

const defaultSetting: ScopeSetting = {
  tenantId: '',
  scopeMode: 'local',
  tenantDefaultScopeKey: null,
  allowMixedMode: true,
  updatedAtUtc: ''
};

const readJson = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `Request failed (${response.status})`);
  }
  return payload as T;
};

const formatSource = (source: ScopeSource): string => {
  if (source === 'spfx_export') return 'SPFx export';
  if (source === 'm365_graph_structure') return 'M365 org structure';
  return 'Portal';
};

const formatMode = (mode: ScopeMode): string => {
  if (mode === 'portal_enforced') return 'Portal enforced';
  if (mode === 'mixed') return 'Mixed';
  return 'Local';
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatPublicationTarget = (event: ScopePublicationEvent): string => {
  if (event.publicationLevel === 'tenant') {
    return event.isDefault ? 'tenant default' : 'tenant-wide';
  }
  if (event.publicationLevel === 'template') {
    return `template ${event.templateKey ?? '-'}`;
  }
  return `instance ${event.instanceId ?? '-'}`;
};

const importHasWarnings = (record: ScopeImportRecord): boolean =>
  Boolean(record.payload?.metadata?.truncated || record.payload?.metadata?.collisions?.length);

const importWarningText = (record: ScopeImportRecord): string[] => {
  const messages: string[] = [];
  if (record.payload?.metadata?.truncated) {
    messages.push(`Microsoft Graph import was capped at ${record.payload.metadata.userLimit ?? 'the configured limit'} users.`);
  }
  const collisionCount = record.payload?.metadata?.collisions?.length ?? 0;
  if (collisionCount > 0) {
    messages.push(`${collisionCount} Scope key collision${collisionCount === 1 ? '' : 's'} resolved during import.`);
  }
  return messages;
};

const defaultImportJson = JSON.stringify({
  entries: [
    {
      localId: 'goods-intake',
      title: 'Goods Intake',
      path: '/Goods Intake',
      sortOrder: 0
    }
  ]
}, null, 2);

const readImportPayload = (raw: string): ScopeImportPayload => {
  const parsed = JSON.parse(raw) as unknown;
  const payload = Array.isArray(parsed) ? { entries: parsed } : parsed;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Import payload must be a JSON object or an entry array.');
  }
  const record = payload as { entries?: unknown; stakeholders?: unknown; metadata?: unknown };
  const entries = Array.isArray(record.entries) ? record.entries : [];
  const stakeholders = Array.isArray(record.stakeholders) ? record.stakeholders : [];
  return {
    entries: entries
      .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object' && !Array.isArray(entry)))
      .map((entry, index) => ({
        localId: String(entry.localId ?? entry.scopeKey ?? `entry-${index + 1}`).trim(),
        scopeKey: String(entry.scopeKey ?? entry.localId ?? `entry-${index + 1}`).trim(),
        parentLocalId: entry.parentLocalId == null ? null : String(entry.parentLocalId).trim(),
        parentScopeKey: entry.parentScopeKey == null ? null : String(entry.parentScopeKey).trim(),
        title: String(entry.title ?? `Scope ${index + 1}`).trim(),
        path: entry.path == null ? null : String(entry.path).trim(),
        sortOrder: typeof entry.sortOrder === 'number' ? entry.sortOrder : index,
        isActive: entry.isActive !== false,
        metadata: entry.metadata && typeof entry.metadata === 'object' && !Array.isArray(entry.metadata)
          ? entry.metadata as Record<string, unknown>
          : undefined
      })),
    stakeholders: stakeholders
      .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object' && !Array.isArray(entry)))
      .map((entry) => ({
        scopeKey: entry.scopeKey == null ? null : String(entry.scopeKey).trim(),
        localId: entry.localId == null ? null : String(entry.localId).trim(),
        stakeholderType: ['portal_contact', 'entra_group', 'sharepoint_group', 'email'].includes(String(entry.stakeholderType))
          ? entry.stakeholderType as ScopeImportStakeholder['stakeholderType']
          : 'email',
        stakeholderRef: String(entry.stakeholderRef ?? '').trim(),
        displayName: entry.displayName == null ? null : String(entry.displayName).trim(),
        bindingMode: ['audit_stakeholder_suggestion', 'notification_default', 'assignment_default'].includes(String(entry.bindingMode))
          ? entry.bindingMode as ScopeImportStakeholder['bindingMode']
          : 'audit_stakeholder_suggestion',
        inheritsToChildren: entry.inheritsToChildren !== false
      })),
    metadata: record.metadata && typeof record.metadata === 'object' && !Array.isArray(record.metadata)
      ? record.metadata as Record<string, unknown>
      : undefined
  };
};

const ScopesAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const sessionTenantId = String(tenant?.tenantId ?? '').trim().toLowerCase();
  const hasAccess = tenant?.capabilities?.canManageScopes === true;

  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [setting, setSetting] = React.useState<ScopeSetting>(defaultSetting);
  const [publishedScopes, setPublishedScopes] = React.useState<ScopeSet[]>([]);
  const [imports, setImports] = React.useState<ScopeImportRecord[]>([]);
  const [instances, setInstances] = React.useState<PortalInstance[]>([]);
  const [statuses, setStatuses] = React.useState<InstanceScopeStatus[]>([]);
  const [publicationHistory, setPublicationHistory] = React.useState<ScopePublicationEvent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);
  const [importName, setImportName] = React.useState('');
  const [importDescription, setImportDescription] = React.useState('');
  const [importSource, setImportSource] = React.useState<ScopeSource>('spfx_export');
  const [sourceInstanceId, setSourceInstanceId] = React.useState('');
  const [importJson, setImportJson] = React.useState(defaultImportJson);
  const [editingImportId, setEditingImportId] = React.useState<string | null>(null);
  const [graphImportName, setGraphImportName] = React.useState('');
  const [graphImporting, setGraphImporting] = React.useState(false);
  const [m365GraphImport, setM365GraphImport] = React.useState<M365GraphImportState | null>(null);
  const [m365GraphImportUpdating, setM365GraphImportUpdating] = React.useState(false);
  const [m365GraphPermissionRequired, setM365GraphPermissionRequired] = React.useState(false);
  const [m365GraphPermissionError, setM365GraphPermissionError] = React.useState<string | null>(null);
  const [m365GraphItRequestCopied, setM365GraphItRequestCopied] = React.useState(false);
  const [templateBindingScopeSetId, setTemplateBindingScopeSetId] = React.useState('');
  const [templateBindingKey, setTemplateBindingKey] = React.useState('');
  const [instanceBindingScopeSetId, setInstanceBindingScopeSetId] = React.useState('');
  const [instanceBindingInstanceId, setInstanceBindingInstanceId] = React.useState('');
  const [instanceBindingConfirmation, setInstanceBindingConfirmation] = React.useState<InstanceBindingConfirmation | null>(null);
  const [lastLoadedSetting, setLastLoadedSetting] = React.useState<ScopeSetting>(defaultSetting);
  const [acknowledgedWarningImports, setAcknowledgedWarningImports] = React.useState<Record<string, boolean>>({});
  const m365GraphImportEnabled = Boolean(m365GraphImport?.available && m365GraphImport.active);

  const draftImportPayload = React.useMemo(() => {
    try {
      return { payload: readImportPayload(importJson), error: null as string | null };
    } catch (err) {
      return { payload: null, error: (err as Error).message };
    }
  }, [importJson]);

  const scopeNameById = React.useMemo(() => {
    const names = new Map<string, string>();
    publishedScopes.forEach((scope) => names.set(scope.scopeSetId, scope.name));
    return names;
  }, [publishedScopes]);

  const activeInstances = React.useMemo(
    () => instances
      .filter((instance) => instance.status !== 'deregistered')
      .sort((left, right) => String(left.siteUrl ?? left.instanceId).localeCompare(String(right.siteUrl ?? right.instanceId))),
    [instances]
  );

  const statusByInstanceId = React.useMemo(() => {
    const map = new Map<string, InstanceScopeStatus>();
    statuses.forEach((status) => map.set(status.instanceId, status));
    return map;
  }, [statuses]);

  const instancePublicationByInstanceId = React.useMemo(() => {
    const map = new Map<string, { scope: ScopeSet; publication: NonNullable<ScopeSet['publications']>[number] }>();
    publishedScopes.forEach((scope) => {
      scope.publications
        ?.filter((publication) =>
          publication.publicationLevel === 'instance' &&
          publication.status !== 'retired' &&
          Boolean(publication.instanceId)
        )
        .forEach((publication) => {
          map.set(publication.instanceId as string, { scope, publication });
        });
    });
    return map;
  }, [publishedScopes]);

  const formatInstanceLabel = (instance: PortalInstance): string => {
    const site = instance.siteUrl || instance.pageUrl || instance.instanceId;
    const version = instance.webPartVersion ? ` · v${instance.webPartVersion}` : '';
    const state = instance.status ? ` · ${instance.status}` : '';
    return `${site}${version}${state}`;
  };

  const formatScopeLabel = (scope: ScopeSet | null | undefined, fallback: string): string =>
    scope ? `${scope.name} v${scope.version}` : fallback;

  const latestScopeByKey = React.useMemo(() => {
    const latest = new Map<string, ScopeSet>();
    publishedScopes.forEach((scope) => {
      const current = latest.get(scope.scopeKey);
      if (!current || scope.version > current.version) {
        latest.set(scope.scopeKey, scope);
      }
    });
    return Array.from(latest.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [publishedScopes]);

  // Workspace state: which Scope the admin is currently looking at, which
  // entry inside it is selected, and the mutable working copy of its
  // entries. Defaults to the tenant default Scope when one is set;
  // otherwise the first available Scope. Resets to the server's entries
  // when the workspace Scope changes or when the user discards edits.
  const [workspaceScopeKey, setWorkspaceScopeKey] = React.useState<string | null>(null);
  const [workspaceSelectedEntryKey, setWorkspaceSelectedEntryKey] = React.useState<string | null>(null);
  const [workspaceEntries, setWorkspaceEntries] = React.useState<ScopeTreeEntry[]>([]);
  const [workspaceBaseline, setWorkspaceBaseline] = React.useState<ScopeTreeEntry[]>([]);
  const [workspaceStakeholders, setWorkspaceStakeholders] = React.useState<EditableStakeholder[]>([]);
  const [workspaceStakeholdersBaseline, setWorkspaceStakeholdersBaseline] = React.useState<EditableStakeholder[]>([]);
  const [publishDiffOpen, setPublishDiffOpen] = React.useState(false);
  const [whereUsedInstanceId, setWhereUsedInstanceId] = React.useState('');
  const [whereUsedLoading, setWhereUsedLoading] = React.useState(false);
  const [whereUsedError, setWhereUsedError] = React.useState<string | null>(null);
  const [whereUsedResult, setWhereUsedResult] = React.useState<ScopeWhereUsedResult | null>(null);

  const workspaceScope = React.useMemo(
    () => latestScopeByKey.find((scope) => scope.scopeKey === workspaceScopeKey) ?? null,
    [latestScopeByKey, workspaceScopeKey]
  );

  // Pick a Scope when the available set changes.
  React.useEffect(() => {
    if (workspaceScopeKey && latestScopeByKey.some((scope) => scope.scopeKey === workspaceScopeKey)) {
      return;
    }
    const preferred = setting.tenantDefaultScopeKey
      && latestScopeByKey.find((scope) => scope.scopeKey === setting.tenantDefaultScopeKey);
    const next = preferred ?? latestScopeByKey[0] ?? null;
    setWorkspaceScopeKey(next ? next.scopeKey : null);
    setWorkspaceSelectedEntryKey(null);
  }, [latestScopeByKey, setting.tenantDefaultScopeKey, workspaceScopeKey]);

  React.useEffect(() => {
    if (whereUsedInstanceId && activeInstances.some((instance) => instance.instanceId === whereUsedInstanceId)) {
      return;
    }
    setWhereUsedInstanceId(activeInstances[0]?.instanceId ?? '');
  }, [activeInstances, whereUsedInstanceId]);

  React.useEffect(() => {
    setWhereUsedResult(null);
    setWhereUsedError(null);
  }, [selectedTenantId, workspaceSelectedEntryKey, whereUsedInstanceId]);

  // Seed/reset the working copy when the workspace Scope changes.
  React.useEffect(() => {
    const next = (workspaceScope?.entries ?? []).map((entry) => ({
      scopeEntryKey: entry.scopeEntryKey,
      parentScopeEntryKey: entry.parentScopeEntryKey,
      title: entry.title,
      sortOrder: entry.sortOrder,
      isActive: entry.isActive
    }));
    setWorkspaceEntries(next);
    setWorkspaceBaseline(next);
    const seedStakeholders = (workspaceScope?.stakeholders ?? []).map((binding, index): EditableStakeholder => ({
      __localId: `srv:${index}:${binding.scopeEntryKey ?? 'scope'}:${binding.stakeholderType}:${binding.stakeholderRef}`,
      scopeEntryKey: binding.scopeEntryKey,
      stakeholderType: binding.stakeholderType,
      stakeholderRef: binding.stakeholderRef,
      displayName: binding.displayName,
      bindingMode: binding.bindingMode,
      inheritsToChildren: binding.inheritsToChildren
    }));
    setWorkspaceStakeholders(seedStakeholders);
    setWorkspaceStakeholdersBaseline(seedStakeholders);
  }, [workspaceScope]);

  const workspaceEntryTitleByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    workspaceEntries.forEach((entry) => map.set(entry.scopeEntryKey, entry.title));
    return map;
  }, [workspaceEntries]);

  const workspaceSelectedEntryAncestors = React.useMemo<string[]>(() => {
    if (!workspaceSelectedEntryKey) return [];
    const byKey = new Map(workspaceEntries.map((entry) => [entry.scopeEntryKey, entry]));
    const ancestors: string[] = [];
    let cursor = byKey.get(workspaceSelectedEntryKey);
    let safety = 0;
    while (cursor && cursor.parentScopeEntryKey && safety < 64) {
      ancestors.push(cursor.parentScopeEntryKey);
      cursor = byKey.get(cursor.parentScopeEntryKey);
      safety += 1;
    }
    return ancestors;
  }, [workspaceEntries, workspaceSelectedEntryKey]);

  const dirtyEntryCount = React.useMemo(
    () => workspaceEntries.filter((entry) => Boolean(entry.__dirty)).length,
    [workspaceEntries]
  );
  const dirtyStakeholderCount = React.useMemo(
    () => workspaceStakeholders.filter((binding) => Boolean(binding.__dirty)).length,
    [workspaceStakeholders]
  );
  const dirtyCount = dirtyEntryCount + dirtyStakeholderCount;

  const markStakeholderDirty = (binding: EditableStakeholder, next: ScopeStakeholderDirtyState): ScopeStakeholderDirtyState => {
    if (binding.__dirty === 'new') return 'new';
    return next;
  };

  const addStakeholder = (binding: Omit<EditableStakeholder, '__localId' | '__dirty'>): void => {
    const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    setWorkspaceStakeholders((previous) => [...previous, {
      ...binding,
      __localId: `new:${id}`,
      __dirty: 'new'
    }]);
  };

  const updateStakeholder = (localId: string, patch: Partial<EditableStakeholder>): void => {
    setWorkspaceStakeholders((previous) => previous.map((binding) => {
      if (binding.__localId !== localId) return binding;
      const next = { ...binding, ...patch };
      next.__dirty = markStakeholderDirty(binding, 'edited');
      return next;
    }));
  };

  const removeStakeholder = (localId: string): void => {
    setWorkspaceStakeholders((previous) => previous.flatMap((binding) => {
      if (binding.__localId !== localId) return [binding];
      // If it was a freshly-added binding, drop it entirely.
      if (binding.__dirty === 'new') return [];
      // Otherwise mark removed so the diff shows the change.
      return [{ ...binding, __dirty: 'removed' }];
    }));
  };

  const refreshWhereUsedCounts = async (): Promise<void> => {
    if (!selectedTenantId || !workspaceSelectedEntryKey || !whereUsedInstanceId) {
      return;
    }
    setWhereUsedLoading(true);
    setWhereUsedError(null);
    try {
      const params = new URLSearchParams({
        tenantId: selectedTenantId,
        instanceId: whereUsedInstanceId
      });
      const response = await fetch(
        `/api/admin/scopes/entries/${encodeURIComponent(workspaceSelectedEntryKey)}/where-used?${params.toString()}`
      );
      const payload = await readJson<ScopeWhereUsedResult>(response);
      setWhereUsedResult(payload);
    } catch (err) {
      setWhereUsedResult(null);
      setWhereUsedError((err as Error).message || 'Unable to load Scope usage counts.');
    } finally {
      setWhereUsedLoading(false);
    }
  };

  const markDirty = (entry: ScopeTreeEntry, next: ScopeEntryDirtyState): ScopeEntryDirtyState => {
    // "new" sticks regardless of further edits; otherwise the new marker wins.
    if (entry.__dirty === 'new') return 'new';
    return next;
  };

  const renameEntry = (key: string, title: string): void => {
    const trimmed = title.replace(/[\r\n]+/g, ' ').slice(0, 256);
    setWorkspaceEntries((previous) => previous.map((entry) => {
      if (entry.scopeEntryKey !== key) return entry;
      if (entry.title === trimmed) return entry;
      const baseline = workspaceBaseline.find((candidate) => candidate.scopeEntryKey === key);
      // If the user undid the rename back to the original title, drop the dirty marker.
      if (baseline && baseline.title === trimmed && entry.__dirty === 'renamed') {
        const { __dirty: _drop, ...rest } = entry;
        return { ...rest, title: trimmed };
      }
      return { ...entry, title: trimmed, __dirty: markDirty(entry, 'renamed') };
    }));
  };

  const addChildEntry = (parentKey: string): void => {
    const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `new-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    const newKey = `new:${id}`;
    setWorkspaceEntries((previous) => {
      const siblings = previous.filter((entry) => entry.parentScopeEntryKey === parentKey);
      const nextSortOrder = siblings.reduce((max, entry) => Math.max(max, entry.sortOrder), -1) + 1;
      return [...previous, {
        scopeEntryKey: newKey,
        parentScopeEntryKey: parentKey,
        title: 'New entry',
        sortOrder: nextSortOrder,
        isActive: true,
        __dirty: 'new'
      }];
    });
    setWorkspaceSelectedEntryKey(newKey);
  };

  const retireEntry = (key: string): void => {
    setWorkspaceEntries((previous) => previous.map((entry) => {
      if (entry.scopeEntryKey !== key) return entry;
      if (!entry.isActive) return entry;
      return { ...entry, isActive: false, __dirty: markDirty(entry, 'retired') };
    }));
  };

  const restoreEntry = (key: string): void => {
    setWorkspaceEntries((previous) => previous.map((entry) => {
      if (entry.scopeEntryKey !== key) return entry;
      if (entry.isActive) return entry;
      return { ...entry, isActive: true, __dirty: markDirty(entry, 'restored') };
    }));
  };

  // Reassign sortOrder for the siblings of one parent so the resulting
  // tree has stable positions after a reorder or reparent.
  const renumberSiblings = (
    entries: ScopeTreeEntry[],
    parentKey: string | null,
    orderedKeys: string[]
  ): ScopeTreeEntry[] => {
    const positionByKey = new Map(orderedKeys.map((key, index) => [key, index]));
    return entries.map((entry) => {
      if ((entry.parentScopeEntryKey ?? null) !== parentKey) return entry;
      const nextSortOrder = positionByKey.get(entry.scopeEntryKey);
      if (nextSortOrder === undefined || nextSortOrder === entry.sortOrder) return entry;
      return { ...entry, sortOrder: nextSortOrder, __dirty: markDirty(entry, 'moved') };
    });
  };

  const reorderEntry = (movedKey: string, beforeSiblingKey: string): void => {
    setWorkspaceEntries((previous) => {
      const moved = previous.find((entry) => entry.scopeEntryKey === movedKey);
      const target = previous.find((entry) => entry.scopeEntryKey === beforeSiblingKey);
      if (!moved || !target) return previous;
      const parentKey = moved.parentScopeEntryKey ?? null;
      const siblings = previous
        .filter((entry) => (entry.parentScopeEntryKey ?? null) === parentKey)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
      const withoutMoved = siblings.filter((entry) => entry.scopeEntryKey !== movedKey);
      const insertAt = withoutMoved.findIndex((entry) => entry.scopeEntryKey === beforeSiblingKey);
      if (insertAt < 0) return previous;
      withoutMoved.splice(insertAt, 0, moved);
      const orderedKeys = withoutMoved.map((entry) => entry.scopeEntryKey);
      return renumberSiblings(previous, parentKey, orderedKeys);
    });
  };

  const reparentEntry = (movedKey: string, newParentKey: string | null, beforeSiblingKey: string): void => {
    setWorkspaceEntries((previous) => {
      const moved = previous.find((entry) => entry.scopeEntryKey === movedKey);
      if (!moved) return previous;
      // Apply the new parent + mark moved.
      const repainted = previous.map((entry) => {
        if (entry.scopeEntryKey !== movedKey) return entry;
        return { ...entry, parentScopeEntryKey: newParentKey, __dirty: markDirty(entry, 'moved') };
      });
      // Renumber the new parent's siblings.
      const siblings = repainted
        .filter((entry) => (entry.parentScopeEntryKey ?? null) === newParentKey)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
      const insertAt = siblings.findIndex((entry) => entry.scopeEntryKey === beforeSiblingKey);
      const withoutMoved = siblings.filter((entry) => entry.scopeEntryKey !== movedKey);
      const movedRepainted = repainted.find((entry) => entry.scopeEntryKey === movedKey)!;
      if (insertAt < 0) {
        withoutMoved.push(movedRepainted);
      } else {
        const targetIndexInWithoutMoved = withoutMoved.findIndex(
          (entry) => entry.scopeEntryKey === beforeSiblingKey
        );
        withoutMoved.splice(targetIndexInWithoutMoved < 0 ? withoutMoved.length : targetIndexInWithoutMoved, 0, movedRepainted);
      }
      const orderedKeys = withoutMoved.map((entry) => entry.scopeEntryKey);
      return renumberSiblings(repainted, newParentKey, orderedKeys);
    });
  };

  const discardEdits = (): void => {
    if (dirtyCount === 0) return;
    const confirmed = window.confirm(`Discard ${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'} on this Scope?`);
    if (!confirmed) return;
    setWorkspaceEntries(workspaceBaseline);
    setWorkspaceStakeholders(workspaceStakeholdersBaseline);
    setWorkspaceSelectedEntryKey(null);
  };

  const openPublishDiff = (): void => {
    if (dirtyCount === 0 || !workspaceScope) return;
    setPublishDiffOpen(true);
  };

  const saveWorkspaceEdits = async (): Promise<void> => {
    if (dirtyCount === 0 || !workspaceScope) {
      return;
    }
    const isTenantDefault = setting.tenantDefaultScopeKey === workspaceScope.scopeKey;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      // Build the import payload from the working copy. New entries use their
      // generated `new:` key as scopeKey/localId so the backend mints a stable
      // identity for them; existing entries keep their durable scopeEntryKey.
      const payload: ScopeImportPayload = {
        entries: workspaceEntries.map((entry) => ({
          localId: entry.scopeEntryKey,
          scopeKey: entry.scopeEntryKey,
          parentLocalId: entry.parentScopeEntryKey,
          parentScopeKey: entry.parentScopeEntryKey,
          title: entry.title,
          path: null,
          sortOrder: entry.sortOrder,
          isActive: entry.isActive
        })),
        stakeholders: workspaceStakeholders
          .filter((stakeholder) => stakeholder.__dirty !== 'removed')
          .map((stakeholder) => ({
            scopeKey: stakeholder.scopeEntryKey,
            localId: stakeholder.scopeEntryKey,
            stakeholderType: stakeholder.stakeholderType,
            stakeholderRef: stakeholder.stakeholderRef,
            displayName: stakeholder.displayName,
            bindingMode: stakeholder.bindingMode,
            inheritsToChildren: stakeholder.inheritsToChildren
          })),
        metadata: {
          revisionOfScopeSetId: workspaceScope.scopeSetId,
          revisionOfScopeKey: workspaceScope.scopeKey,
          revisionOfVersion: workspaceScope.version
        }
      };
      const draftResponse = await fetch('/api/admin/scopes/imports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          proposedName: workspaceScope.name,
          description: workspaceScope.description,
          importSource: 'portal',
          sourceInstanceId: null,
          payload
        })
      });
      const draft = await readJson<{ import?: { scopeImportId: string } }>(draftResponse);
      const scopeImportId = draft.import?.scopeImportId;
      if (!scopeImportId) {
        throw new Error('Could not start a revision draft for this Scope.');
      }
      const publishResponse = await fetch(`/api/admin/scopes/imports/${encodeURIComponent(scopeImportId)}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          name: workspaceScope.name,
          description: workspaceScope.description,
          publications: [{ publicationLevel: 'tenant', isDefault: isTenantDefault }]
        })
      });
      await readJson(publishResponse);
      setSavedMessage(`Published v${workspaceScope.version + 1} of "${workspaceScope.name}".`);
      setWorkspaceSelectedEntryKey(null);
      setPublishDiffOpen(false);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess || !selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSavedMessage(null);
    try {
      const [publishedResponse, importsResponse, statusesResponse, historyResponse, graphStatusResponse] = await Promise.all([
        fetch(`/api/admin/scopes/published?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/scopes/imports?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/scopes/instances/status?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/scopes/publications/history?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/scopes/imports/m365-graph/status?tenantId=${encodeURIComponent(selectedTenantId)}`)
      ]);
      const instancesResponse = await fetch(`/api/instances?tenantId=${encodeURIComponent(selectedTenantId)}`);
      const publishedPayload = await readJson<{ setting?: ScopeSetting; scopes?: ScopeSet[] }>(publishedResponse);
      const importsPayload = await readJson<{ imports?: ScopeImportRecord[] }>(importsResponse);
      const statusesPayload = await readJson<{ statuses?: InstanceScopeStatus[] }>(statusesResponse);
      const historyPayload = await readJson<{ events?: ScopePublicationEvent[] }>(historyResponse);
      const graphStatus = await readJson<M365GraphImportState>(graphStatusResponse);
      const instancesPayload = await readJson<{ instances?: PortalInstance[] }>(instancesResponse);
      const loadedSetting = { ...defaultSetting, ...(publishedPayload.setting ?? {}) };
      setSetting(loadedSetting);
      setLastLoadedSetting(loadedSetting);
      setPublishedScopes(Array.isArray(publishedPayload.scopes) ? publishedPayload.scopes : []);
      setImports(Array.isArray(importsPayload.imports) ? importsPayload.imports : []);
      setInstances(Array.isArray(instancesPayload.instances) ? instancesPayload.instances : []);
      setAcknowledgedWarningImports((previous) => {
        const next: Record<string, boolean> = {};
        for (const record of Array.isArray(importsPayload.imports) ? importsPayload.imports : []) {
          if (previous[record.scopeImportId]) {
            next[record.scopeImportId] = true;
          }
        }
        return next;
      });
      setStatuses(Array.isArray(statusesPayload.statuses) ? statusesPayload.statuses : []);
      setPublicationHistory(Array.isArray(historyPayload.events) ? historyPayload.events : []);
      setM365GraphImport(graphStatus);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, selectedTenantId]);

  React.useEffect(() => {
    const loadTenants = async (): Promise<void> => {
      if (!hasAccess) return;
      try {
        const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
        const payload = await readJson<{ tenants?: AdminTenantOption[]; defaultTenantId?: string }>(response);
        const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
        setDeployableTenants(tenants);
        setSelectedTenantId(payload.defaultTenantId || tenants[0]?.tenantId || sessionTenantId);
      } catch (err) {
        setError((err as Error).message);
      }
    };
    void loadTenants();
  }, [hasAccess, sessionTenantId]);

  React.useEffect(() => {
    setM365GraphPermissionRequired(false);
    setM365GraphPermissionError(null);
    setM365GraphItRequestCopied(false);
    void load();
  }, [load]);

  const saveSettings = async (): Promise<void> => {
    if (setting.scopeMode !== lastLoadedSetting.scopeMode) {
      const confirmed = window.confirm(
        `Change Scope mode from ${formatMode(lastLoadedSetting.scopeMode)} to ${formatMode(setting.scopeMode)} for this tenant?`
      );
      if (!confirmed) {
        return;
      }
    }
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch('/api/admin/scopes/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          scopeMode: setting.scopeMode,
          tenantDefaultScopeKey: setting.tenantDefaultScopeKey || null,
          allowMixedMode: setting.allowMixedMode
        })
      });
      const payload = await readJson<{ setting?: ScopeSetting }>(response);
      if (payload.setting) {
        const savedSetting = { ...defaultSetting, ...payload.setting };
        setSetting(savedSetting);
        setLastLoadedSetting(savedSetting);
      }
      setSavedMessage('Scope settings saved.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const createDraftImport = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const payload = readImportPayload(importJson);
      const response = await fetch(
        editingImportId
          ? `/api/admin/scopes/imports/${encodeURIComponent(editingImportId)}`
          : '/api/admin/scopes/imports',
        {
          method: editingImportId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId: selectedTenantId,
            proposedName: importName,
            description: importDescription,
            importSource,
            sourceInstanceId: sourceInstanceId || null,
            payload
          })
        }
      );
      await readJson(response);
      setSavedMessage(editingImportId ? 'Scope import draft updated.' : 'Scope import draft created.');
      setImportName('');
      setImportDescription('');
      setSourceInstanceId('');
      setEditingImportId(null);
      setImportJson(defaultImportJson);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const loadImportForEditing = (record: ScopeImportRecord): void => {
    setEditingImportId(record.scopeImportId);
    setImportName(record.proposedName);
    setImportDescription(record.description ?? '');
    setImportSource(record.importSource);
    setSourceInstanceId(record.sourceInstanceId ?? '');
    setImportJson(JSON.stringify(record.payload ?? { entries: [] }, null, 2));
    setSavedMessage(null);
    setError(null);
  };

  const cancelImportEditing = (): void => {
    setEditingImportId(null);
    setImportName('');
    setImportDescription('');
    setSourceInstanceId('');
    setImportJson(defaultImportJson);
  };

  const createRevisionDraftFromPublished = async (scope: ScopeSet): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const payload: ScopeImportPayload = {
        entries: scope.entries.map((entry) => ({
          localId: entry.scopeEntryKey,
          scopeKey: entry.scopeEntryKey,
          parentLocalId: entry.parentScopeEntryKey,
          parentScopeKey: entry.parentScopeEntryKey,
          title: entry.title,
          path: entry.path,
          sortOrder: entry.sortOrder,
          isActive: entry.isActive
        })),
        stakeholders: (scope.stakeholders ?? []).map((stakeholder) => ({
          scopeKey: stakeholder.scopeEntryKey,
          localId: stakeholder.scopeEntryKey,
          stakeholderType: stakeholder.stakeholderType,
          stakeholderRef: stakeholder.stakeholderRef,
          displayName: stakeholder.displayName,
          bindingMode: stakeholder.bindingMode,
          inheritsToChildren: stakeholder.inheritsToChildren
        })),
        metadata: {
          revisionOfScopeSetId: scope.scopeSetId,
          revisionOfScopeKey: scope.scopeKey,
          revisionOfVersion: scope.version
        }
      };
      const response = await fetch('/api/admin/scopes/imports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          proposedName: scope.name,
          description: scope.description,
          importSource: 'portal',
          sourceInstanceId: null,
          payload
        })
      });
      await readJson(response);
      setSavedMessage(`Revision draft created for ${scope.name} v${scope.version}.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const replaceDraftImportPayload = (next: ScopeImportPayload): void => {
    setImportJson(JSON.stringify(next, null, 2));
  };

  const updateDraftEntry = (index: number, patch: Partial<ScopeImportEntry>): void => {
    const payload = draftImportPayload.payload;
    if (!payload) return;
    const entries = payload.entries.map((entry, entryIndex) => entryIndex === index ? { ...entry, ...patch } : entry);
    replaceDraftImportPayload({ ...payload, entries });
  };

  const addDraftEntry = (): void => {
    const payload = draftImportPayload.payload ?? { entries: [] };
    const nextIndex = payload.entries.length + 1;
    replaceDraftImportPayload({
      ...payload,
      entries: [
        ...payload.entries,
        {
          localId: `scope-${nextIndex}`,
          scopeKey: `scope-${nextIndex}`,
          title: `Scope ${nextIndex}`,
          path: `/Scope ${nextIndex}`,
          sortOrder: payload.entries.length,
          isActive: true
        }
      ]
    });
  };

  const removeDraftEntry = (index: number): void => {
    const payload = draftImportPayload.payload;
    if (!payload) return;
    replaceDraftImportPayload({
      ...payload,
      entries: payload.entries.filter((_entry, entryIndex) => entryIndex !== index)
    });
  };

  const updateDraftStakeholder = (index: number, patch: Partial<ScopeImportStakeholder>): void => {
    const payload = draftImportPayload.payload;
    if (!payload) return;
    const stakeholders = (payload.stakeholders ?? []).map((stakeholder, stakeholderIndex) =>
      stakeholderIndex === index ? { ...stakeholder, ...patch } : stakeholder
    );
    replaceDraftImportPayload({ ...payload, stakeholders });
  };

  const addDraftStakeholder = (): void => {
    const payload = draftImportPayload.payload ?? { entries: [] };
    replaceDraftImportPayload({
      ...payload,
      stakeholders: [
        ...(payload.stakeholders ?? []),
        {
          scopeKey: payload.entries[0]?.scopeKey ?? payload.entries[0]?.localId ?? null,
          stakeholderType: 'email',
          stakeholderRef: '',
          displayName: '',
          bindingMode: 'audit_stakeholder_suggestion',
          inheritsToChildren: true
        }
      ]
    });
  };

  const removeDraftStakeholder = (index: number): void => {
    const payload = draftImportPayload.payload;
    if (!payload) return;
    replaceDraftImportPayload({
      ...payload,
      stakeholders: (payload.stakeholders ?? []).filter((_stakeholder, stakeholderIndex) => stakeholderIndex !== index)
    });
  };

  const setM365GraphImportActive = async (active: boolean): Promise<void> => {
    if (!selectedTenantId) return;
    setM365GraphImportUpdating(true);
    setM365GraphPermissionError(null);
    try {
      const response = await fetch('/api/admin/scopes/imports/m365-graph/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, active })
      });
      await readJson(response);
      if (!active) {
        setM365GraphPermissionRequired(false);
        setM365GraphItRequestCopied(false);
      }
      await load();
    } catch (err) {
      setM365GraphPermissionError((err as Error).message);
    } finally {
      setM365GraphImportUpdating(false);
    }
  };

  const copyM365GraphPermissionRequest = async (): Promise<void> => {
    if (!m365GraphImport?.clientId) return;
    const request = [
      'Please approve optional Microsoft 365 Scope import for Edinshall Audit.',
      `Tenant: ${selectedTenantId}`,
      `Application client ID: ${m365GraphImport.clientId}`,
      `Delegated Microsoft Graph permissions: ${m365GraphImport.permissions.join(', ')}`,
      'Purpose: allow an authorised Scope administrator to create a reviewable draft Scope from organisation, department, and office-location directory fields.',
      'The feature performs no Graph writes, is off by default for the tenant, and manual/SharePoint Scope creation remains available without it.'
    ].join('\n');
    try {
      await navigator.clipboard.writeText(request);
      setM365GraphItRequestCopied(true);
    } catch {
      setM365GraphPermissionError('The IT permission request could not be copied. Copy the tenant, application client ID, and permissions shown here manually.');
    }
  };

  const createGraphImportDraft = async (proposedName = graphImportName, interactive = false): Promise<void> => {
    if (!m365GraphImport?.active || !m365GraphImport.clientId) return;
    setGraphImporting(true);
    setError(null);
    setM365GraphPermissionError(null);
    setSavedMessage(null);
    try {
      const scopes = m365GraphImport.permissions.map(permission => `https://graph.microsoft.com/${permission}`);
      const accessToken = interactive
        ? await acquireGraphFeatureTokenInteractively({
            clientId: m365GraphImport.clientId,
            tenantId: selectedTenantId,
            scopes
          })
        : await acquireGraphFeatureTokenSilently({
            clientId: m365GraphImport.clientId,
            tenantId: selectedTenantId,
            scopes,
            loginHint: tenant?.userEmail
          });
      if (!accessToken) {
        setM365GraphPermissionRequired(true);
        return;
      }
      const response = await fetch('/api/admin/scopes/imports/m365-graph', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [m365GraphImport.tokenHeaderName]: accessToken
        },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          proposedName
        })
      });
      await readJson(response);
      setM365GraphPermissionRequired(false);
      setGraphImportName('');
      setSavedMessage('Microsoft 365 organisation import draft created.');
      await load();
    } catch (err) {
      setM365GraphPermissionRequired(true);
      setM365GraphPermissionError((err as Error).message);
    } finally {
      setGraphImporting(false);
    }
  };

  const publishImport = async (record: ScopeImportRecord, makeDefault: boolean): Promise<void> => {
    if (importHasWarnings(record) && !acknowledgedWarningImports[record.scopeImportId]) {
      setError('Review and acknowledge the import warnings before publishing this Scope.');
      return;
    }
    const revisionText = record.payload?.metadata?.revisionOfScopeKey
      ? ` This replaces ${record.payload.metadata.revisionOfScopeKey} v${record.payload.metadata.revisionOfVersion ?? '-'}.`
      : '';
    const confirmed = window.confirm(
      makeDefault
        ? `Publish "${record.proposedName}" and make it the tenant default Scope?${revisionText}`
        : `Publish "${record.proposedName}" for this tenant?${revisionText}`
    );
    if (!confirmed) {
      return;
    }
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch(`/api/admin/scopes/imports/${encodeURIComponent(record.scopeImportId)}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          name: record.proposedName,
          description: record.description,
          publications: [{ publicationLevel: 'tenant', isDefault: makeDefault }]
        })
      });
      await readJson(response);
      setSavedMessage(makeDefault ? 'Scope published as tenant default.' : 'Scope published.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const bindScopeToTemplate = async (): Promise<void> => {
    const scopeSetId = templateBindingScopeSetId.trim();
    const templateKey = templateBindingKey.trim();
    if (!scopeSetId || !templateKey) {
      setError('Select a Scope and enter a template key.');
      return;
    }
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch(`/api/admin/scopes/published/${encodeURIComponent(scopeSetId)}/publications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          publicationLevel: 'template',
          templateKey
        })
      });
      await readJson(response);
      setSavedMessage(`Scope bound to template ${templateKey}.`);
      setTemplateBindingKey('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const requestBindScopeToInstance = (): void => {
    const scopeSetId = instanceBindingScopeSetId.trim();
    const instanceId = instanceBindingInstanceId.trim();
    if (!scopeSetId || !instanceId) {
      setError('Select a Scope and an installation.');
      return;
    }
    const instance = activeInstances.find((entry) => entry.instanceId === instanceId);
    const scope = publishedScopes.find((entry) => entry.scopeSetId === scopeSetId);
    if (!instance || !scope) {
      setError('Select a valid Scope and installation.');
      return;
    }
    const current = instancePublicationByInstanceId.get(instanceId);
    setError(null);
    setInstanceBindingConfirmation({
      scopeSetId,
      instanceId,
      scopeLabel: formatScopeLabel(scope, scopeSetId),
      instanceLabel: formatInstanceLabel(instance),
      previousScopeLabel: current ? formatScopeLabel(current.scope, current.scope.scopeSetId) : null
    });
  };

  const confirmBindScopeToInstance = async (): Promise<void> => {
    if (!instanceBindingConfirmation) {
      return;
    }
    const { scopeSetId, instanceId, instanceLabel, scopeLabel } = instanceBindingConfirmation;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch(`/api/admin/scopes/published/${encodeURIComponent(scopeSetId)}/publications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          publicationLevel: 'instance',
          instanceId
        })
      });
      await readJson(response);
      await load();
      setInstanceBindingConfirmation(null);
      setSavedMessage(`${scopeLabel} is now bound to ${instanceLabel}.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const renderWhereUsedCard = (): JSX.Element | null => {
    if (!workspaceSelectedEntryKey || !workspaceScope) {
      return null;
    }
    const selectedInstance = activeInstances.find((instance) => instance.instanceId === whereUsedInstanceId);
    const failedFields = whereUsedResult
      ? [
          ...whereUsedResult.counts.audits.failedFields.map((field) => `Audits.${field.fieldName}`),
          ...whereUsedResult.counts.findings.failedFields.map((field) => `Findings.${field.fieldName}`),
          ...whereUsedResult.counts.actions.failedFields.map((field) => `Actions.${field.fieldName}`)
        ]
      : [];

    return (
      <div className="scope-where-used">
        <div className="scope-where-used-header">
          <div>
            <h4>Where used</h4>
            <p className="muted">
              Live counts for the one selected SharePoint installation. This is not a tenant-wide or cross-installation total.
            </p>
          </div>
          <button
            className="btn secondary btn-small"
            type="button"
            onClick={() => { refreshWhereUsedCounts().catch(() => undefined); }}
            disabled={whereUsedLoading || !whereUsedInstanceId}
          >
            {whereUsedLoading ? 'Scanning...' : 'Refresh counts'}
          </button>
        </div>
        <label className="scope-where-used-instance">
          <span className="muted">Installation</span>
          <select
            className="input"
            value={whereUsedInstanceId}
            disabled={whereUsedLoading || activeInstances.length === 0}
            onChange={(event) => setWhereUsedInstanceId(event.target.value)}
          >
            {activeInstances.length === 0 && <option value="">No active installations</option>}
            {activeInstances.map((instance) => (
              <option key={instance.instanceId} value={instance.instanceId}>
                {formatInstanceLabel(instance)}
              </option>
            ))}
          </select>
        </label>
        {selectedInstance && (
          <div className="muted">
            Scanning {selectedInstance.siteUrl || selectedInstance.pageUrl || selectedInstance.instanceId}
          </div>
        )}
        <div className="scope-where-used-results" role="status" aria-live="polite" aria-busy={whereUsedLoading}>
          {whereUsedLoading && <div className="scope-where-used-empty">Scanning SharePoint lists...</div>}
          {whereUsedError && <div className="scope-where-used-error">{whereUsedError}</div>}
          {!whereUsedError && !whereUsedResult && !whereUsedLoading && (
            <div className="scope-where-used-empty">
              Refresh counts before retiring or renaming this Scope entry.
            </div>
          )}
          {whereUsedResult && (
            <>
              <div className="scope-where-used-counts">
                <div>
                  <strong>{whereUsedResult.total}</strong>
                  <span>Total in selected installation</span>
                </div>
                <div>
                  <strong>{whereUsedResult.counts.audits.count}</strong>
                  <span>Audits</span>
                </div>
                <div>
                  <strong>{whereUsedResult.counts.findings.count}</strong>
                  <span>Findings</span>
                </div>
                <div>
                  <strong>{whereUsedResult.counts.actions.count}</strong>
                  <span>Actions</span>
                </div>
              </div>
              <div className="muted">Fetched {formatDate(whereUsedResult.fetchedAt)}</div>
              {whereUsedResult.partial && (
                <div className="scope-where-used-warning">
                  Partial scan. Failed fields: {failedFields.length ? failedFields.join(', ') : 'unknown'}.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">Scopes</h1>
        <p className="muted">You do not have permission to manage Scopes.</p>
      </div>
    );
  }

  const showEmptyState = latestScopeByKey.length === 0 && imports.length === 0;
  const focusImportDraft = (): void => {
    const node = document.getElementById('scopes-import-draft');
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const startFromBlank = (): void => {
    setEditingImportId(null);
    setImportName('');
    setImportDescription('');
    setImportSource('portal');
    setSourceInstanceId('');
    setImportJson(defaultImportJson);
    focusImportDraft();
  };
  const importFromM365 = async (): Promise<void> => {
    const name = window.prompt(
      'Name for this Scope import (e.g. "Acme organisation 2026"):',
      graphImportName || 'Microsoft 365 organisation'
    );
    if (!name) {
      return;
    }
    setGraphImportName(name);
    await createGraphImportDraft(name);
  };
  const m365AdminConsentUrl = m365GraphImport?.clientId && selectedTenantId
    ? `https://login.microsoftonline.com/${encodeURIComponent(selectedTenantId)}/adminconsent?client_id=${encodeURIComponent(m365GraphImport.clientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth-redirect.html`)}`
    : '';

  return (
    <div>
      <div className="scopes-page-header">
        <h1 className="section-title">Scopes</h1>
        <ScopeModeBadge mode={setting.scopeMode} />
        <span className="spacer" />
        <AdminTenantSelector
          value={selectedTenantId}
          options={deployableTenants}
          onChange={setSelectedTenantId}
          disabled={saving || loading}
        />
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Manage the Scope structures that SPFx installations, templates, reports, and Portal permissions use for this tenant.
      </p>

      {m365GraphImport?.available && (
        <div className="card" style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ maxWidth: '760px' }}>
              <h3 style={{ marginTop: 0 }}>Optional Microsoft 365 Scope import</h3>
              <p className="muted">
                Off by default. When enabled, a Scope administrator can create a reviewable draft from Microsoft 365 organisation, department, and office-location fields. Edinshall requests delegated <strong>User.Read.All</strong> and <strong>Organization.Read.All</strong> only when an administrator starts an import; it performs no Graph writes, and ordinary Scope work remains available without it.
              </p>
              <p className="muted">
                Your Microsoft 365 policy may require an IT administrator to approve these directory-read permissions.
              </p>
            </div>
            <button
              className={m365GraphImport.active ? 'btn secondary' : 'btn'}
              type="button"
              onClick={() => { void setM365GraphImportActive(!m365GraphImport.active); }}
              disabled={m365GraphImportUpdating || graphImporting || saving}
            >
              {m365GraphImportUpdating
                ? 'Updating...'
                : m365GraphImport.active
                  ? 'Turn off Microsoft 365 import'
                  : 'Enable Microsoft 365 import'}
            </button>
          </div>
          {m365GraphImport.active && !m365GraphPermissionRequired && (
            <div className="pill success">Active for this tenant. The first import checks silently for existing consent.</div>
          )}
          {m365GraphPermissionRequired && (
            <div className="notice warning" style={{ marginTop: '10px' }}>
              <strong>Microsoft 365 permission is needed before the import can run.</strong>
              <p>
                Continue to Microsoft to sign in and request the two directory-read permissions. If your organisation blocks user consent, copy the request for IT or open the administrator approval link, then return here and retry.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  type="button"
                  onClick={() => { void createGraphImportDraft(graphImportName, true); }}
                  disabled={graphImporting}
                >
                  {graphImporting ? 'Connecting...' : 'Connect Microsoft 365 and import'}
                </button>
                <button className="btn secondary" type="button" onClick={() => { void copyM365GraphPermissionRequest(); }}>
                  {m365GraphItRequestCopied ? 'IT request copied' : 'Copy request for IT'}
                </button>
                {m365AdminConsentUrl && (
                  <a className="btn secondary" href={m365AdminConsentUrl} target="_blank" rel="noreferrer">
                    Open administrator approval
                  </a>
                )}
              </div>
            </div>
          )}
          {m365GraphPermissionError && <div className="notice error" style={{ marginTop: '10px' }}>{m365GraphPermissionError}</div>}
        </div>
      )}

      <ul className="scopes-lifecycle" aria-label="Scope lifecycle">
        <li className="step">
          <span className="index">1</span>
          <div>
            <strong>Create or import</strong>
            <span>Start blank or export local SPFx Scopes{m365GraphImportEnabled ? ', with optional Microsoft 365 import when separately connected' : ''}.</span>
          </div>
        </li>
        <li className="step">
          <span className="index">2</span>
          <div>
            <strong>Publish</strong>
            <span>Review entries and stakeholders, then publish a versioned Scope.</span>
          </div>
        </li>
        <li className="step">
          <span className="index">3</span>
          <div>
            <strong>Assign</strong>
            <span>Use a tenant default, template binding, or installation binding.</span>
          </div>
        </li>
        <li className="step">
          <span className="index">4</span>
          <div>
            <strong>Enforce and monitor</strong>
            <span>Set the tenant mode and check each installation report.</span>
          </div>
        </li>
      </ul>

      <ScopeDriftBanner statuses={statuses} />

      {showEmptyState ? (
        <ScopesEmptyState
          hasTenant={Boolean(selectedTenantId)}
          isImporting={graphImporting || saving}
          m365ImportEnabled={m365GraphImportEnabled}
          onImportFromM365={() => { void importFromM365(); }}
          onStartBlank={startFromBlank}
          onOpenAdvanced={focusImportDraft}
        />
      ) : (
        <div className="scopes-workspace">
          <div className="scopes-tree-card">
            <div className="scopes-tree-header">
              <h3>Scope</h3>
              <div className="scopes-tree-picker">
                <label className="muted" style={{ fontSize: '12px' }}>Showing</label>
                <select
                  className="input"
                  value={workspaceScopeKey ?? ''}
                  onChange={(event) => {
                    setWorkspaceScopeKey(event.target.value || null);
                    setWorkspaceSelectedEntryKey(null);
                  }}
                  disabled={latestScopeByKey.length === 0}
                >
                  {latestScopeByKey.length === 0 && <option value="">No Scopes yet</option>}
                  {latestScopeByKey.map((scope) => (
                    <option key={scope.scopeKey} value={scope.scopeKey}>
                      {scope.name} v{scope.version}
                      {setting.tenantDefaultScopeKey === scope.scopeKey ? ' · tenant default' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {latestScopeByKey.length === 0 ? (
              <div className="scopes-tree-empty">
                No published Scopes yet. Use the Import Draft section below to publish one.
              </div>
            ) : workspaceScope ? (
              <ScopeTreeView
                entries={workspaceEntries}
                selectedKey={workspaceSelectedEntryKey}
                onSelect={setWorkspaceSelectedEntryKey}
                onReorder={saving ? undefined : reorderEntry}
                onReparent={saving ? undefined : reparentEntry}
              />
            ) : (
              <div className="scopes-tree-empty">Pick a Scope to view its entries.</div>
            )}
          </div>
          <div className="scopes-detail-card">
            <div className="scopes-detail-header">
              <h3>{workspaceSelectedEntryKey ? 'Entry' : 'Whole Scope'}</h3>
              {workspaceScope && (
                <span className="badge-subtle">{workspaceScope.name} v{workspaceScope.version}</span>
              )}
            </div>
            <ScopeEntryDetail
              entries={workspaceEntries}
              selectedKey={workspaceSelectedEntryKey}
              readOnly={!workspaceScope || saving}
              onRename={renameEntry}
              onAddChild={addChildEntry}
              onRetire={retireEntry}
              onRestore={restoreEntry}
            />
            {renderWhereUsedCard()}
            {workspaceScope && (
              <ScopeStakeholderList
                selectedKey={workspaceSelectedEntryKey}
                stakeholders={workspaceStakeholders}
                entryTitleByKey={workspaceEntryTitleByKey}
                entryAncestors={workspaceSelectedEntryAncestors}
                readOnly={saving}
                onAdd={addStakeholder}
                onUpdate={updateStakeholder}
                onRemove={removeStakeholder}
              />
            )}
          </div>
        </div>
      )}

      {dirtyCount > 0 && workspaceScope && (
        <div className="scopes-save-bar" role="status">
          <div className="summary">
            <strong>{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'} on "{workspaceScope.name}".</strong>
            <span className="muted">
              Saving publishes v{workspaceScope.version + 1} of this Scope for your tenant.
            </span>
          </div>
          <div className="actions">
            <button className="btn secondary" type="button" onClick={discardEdits} disabled={saving}>
              Discard
            </button>
            <button className="btn" type="button" onClick={openPublishDiff} disabled={saving}>
              Review and publish
            </button>
          </div>
        </div>
      )}

      {workspaceScope && (
        <ScopePublishDiffModal
          open={publishDiffOpen}
          scopeName={workspaceScope.name}
          fromVersion={workspaceScope.version}
          isTenantDefault={setting.tenantDefaultScopeKey === workspaceScope.scopeKey}
          baseline={workspaceBaseline}
          current={workspaceEntries}
          stakeholderBaseline={workspaceStakeholdersBaseline}
          stakeholderCurrent={workspaceStakeholders}
          saving={saving}
          onCancel={() => { if (!saving) setPublishDiffOpen(false); }}
          onConfirm={() => { void saveWorkspaceEdits(); }}
        />
      )}

      {instanceBindingConfirmation && (
        <div
          className="scopes-modal-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !saving) {
              setInstanceBindingConfirmation(null);
            }
          }}
        >
          <div className="scopes-modal" role="dialog" aria-labelledby="scope-instance-bind-title" aria-modal="true">
            <div className="scopes-modal-header">
              <h3 id="scope-instance-bind-title">Bind Scope to installation</h3>
              <p className="muted">Confirm the active Scope change for this SPFx installation.</p>
            </div>
            <div className="scopes-modal-body">
              <section className="scopes-diff-group tone-warning">
                <h4>Binding change</h4>
                <ul>
                  <li>
                    <span className="primary">Installation</span>
                    <span className="secondary">{instanceBindingConfirmation.instanceLabel}</span>
                  </li>
                  <li>
                    <span className="primary">Current installation binding</span>
                    <span className="secondary">
                      {instanceBindingConfirmation.previousScopeLabel ?? 'None. Template or tenant-default resolution currently applies.'}
                    </span>
                  </li>
                  <li>
                    <span className="primary">New installation binding</span>
                    <span className="secondary">{instanceBindingConfirmation.scopeLabel}</span>
                  </li>
                </ul>
              </section>
              <p className="muted">
                Installation bindings take precedence over template bindings and the tenant default.
                Published revisions are not picked up automatically; re-bind this installation when it should move to a newer version.
              </p>
            </div>
            <div className="scopes-modal-footer">
              <button className="btn secondary" type="button" onClick={() => setInstanceBindingConfirmation(null)} disabled={saving}>
                Cancel
              </button>
              <button className="btn" type="button" onClick={() => { void confirmBindScopeToInstance(); }} disabled={saving}>
                {saving ? 'Binding...' : 'Confirm binding'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Mode</h3>
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Scope mode</div>
            <select
              className="input"
              value={setting.scopeMode}
              onChange={(event) => setSetting((prev) => {
                const scopeMode = event.target.value as ScopeMode;
                return {
                  ...prev,
                  scopeMode,
                  allowMixedMode: scopeMode === 'mixed' ? true : prev.allowMixedMode
                };
              })}
              disabled={saving || loading}
            >
              <option value="local">Local</option>
              <option value="mixed">Mixed</option>
              <option value="portal_enforced">Portal enforced</option>
            </select>
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Tenant default Scope</div>
            <select
              className="input"
              value={setting.tenantDefaultScopeKey ?? ''}
              onChange={(event) => setSetting((prev) => ({ ...prev, tenantDefaultScopeKey: event.target.value || null }))}
              disabled={saving || loading}
            >
              <option value="">No default</option>
              {latestScopeByKey.map((scope) => (
                <option key={scope.scopeKey} value={scope.scopeKey}>{scope.name} v{scope.version}</option>
              ))}
            </select>
          </label>
        </div>
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
          <input
            type="checkbox"
            checked={setting.allowMixedMode}
            onChange={(event) => setSetting((prev) => ({
              ...prev,
              allowMixedMode: event.target.checked,
              scopeMode: !event.target.checked && prev.scopeMode === 'mixed' ? 'local' : prev.scopeMode
            }))}
            disabled={saving || loading || setting.scopeMode === 'portal_enforced'}
          />
          <span>Allow mixed local and Portal Scopes</span>
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
          <button className="btn" type="button" onClick={() => { void saveSettings(); }} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save settings'}
          </button>
          <button className="btn secondary" type="button" onClick={() => { void load(); }} disabled={saving || loading}>
            Reload
          </button>
          <span className="badge-subtle">Current mode: {formatMode(setting.scopeMode)}</span>
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Published Scopes</h3>
        {publishedScopes.length === 0 && <span className="badge-subtle">No published Scopes.</span>}
        <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
          {publishedScopes.map((scope) => (
            <div key={scope.scopeSetId} className="list-row">
              <div>
                <strong>{scope.name}</strong>
                <div className="muted">
                  {formatSource(scope.source)} · v{scope.version} · {scope.entries.length} entries
                </div>
                {scope.description && <div className="muted">{scope.description}</div>}
                {scope.publications?.filter((publication) => publication.publicationLevel === 'template' && publication.templateKey).map((publication) => (
                  <div className="muted" key={publication.publicationId}>
                    Template: {publication.templateKey}
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'right' }}>
                {scope.scopeKey === setting.tenantDefaultScopeKey && <span className="pill success">default</span>}
                {scope.publications?.some((publication) => publication.isDefault) && scope.scopeKey !== setting.tenantDefaultScopeKey && (
                  <span className="badge-subtle">published default</span>
                )}
                <div style={{ marginTop: '8px' }}>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => { void createRevisionDraftFromPublished(scope); }}
                    disabled={saving || loading}
                  >
                    Create revision draft
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Template Binding</h3>
        <p className="muted">
          Choose the Scope that should be used when an audit template starts from a known part of the business.
        </p>
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Published Scope</div>
            <select
              className="input"
              value={templateBindingScopeSetId}
              onChange={(event) => setTemplateBindingScopeSetId(event.target.value)}
              disabled={saving || loading || publishedScopes.length === 0}
            >
              <option value="">Select a Scope</option>
              {publishedScopes.map((scope) => (
                <option key={scope.scopeSetId} value={scope.scopeSetId}>{scope.name}</option>
              ))}
            </select>
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Template key</div>
            <input
              className="input"
              value={templateBindingKey}
              onChange={(event) => setTemplateBindingKey(event.target.value)}
              placeholder="goods-intake"
              disabled={saving || loading}
            />
          </label>
        </div>
        <button
          className="btn"
          type="button"
          onClick={() => { void bindScopeToTemplate(); }}
          disabled={saving || loading || !templateBindingScopeSetId || !templateBindingKey.trim()}
        >
          Bind Scope to template
        </button>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Installation Binding</h3>
        <p className="muted">
          Choose the Portal Scope an SPFx installation should use. Installation binding wins over template binding and tenant default.
          A published revision stays separate until this installation is explicitly rebound.
        </p>
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Published Scope</div>
            <select
              className="input"
              value={instanceBindingScopeSetId}
              onChange={(event) => setInstanceBindingScopeSetId(event.target.value)}
              disabled={saving || loading || publishedScopes.length === 0}
            >
              <option value="">Select a Scope</option>
              {publishedScopes.map((scope) => (
                <option key={scope.scopeSetId} value={scope.scopeSetId}>
                  {scope.name} v{scope.version}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Installation</div>
            <select
              className="input"
              value={instanceBindingInstanceId}
              onChange={(event) => setInstanceBindingInstanceId(event.target.value)}
              disabled={saving || loading || activeInstances.length === 0}
            >
              <option value="">Select an installation</option>
              {activeInstances.map((instance) => (
                <option key={instance.instanceId} value={instance.instanceId}>
                  {formatInstanceLabel(instance)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          className="btn"
          type="button"
          onClick={requestBindScopeToInstance}
          disabled={saving || loading || !instanceBindingScopeSetId || !instanceBindingInstanceId}
        >
          Bind Scope to installation
        </button>
        {publishedScopes.length === 0 && (
          <p className="muted" style={{ marginTop: '8px' }}>
            Publish a Scope before binding one to an installation.
          </p>
        )}
        {activeInstances.length === 0 && (
          <p className="muted" style={{ marginTop: '8px' }}>
            No active installations are registered for this tenant yet.
          </p>
        )}
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Publish History</h3>
        {publicationHistory.length === 0 && <span className="badge-subtle">No Scope publish changes recorded yet.</span>}
        <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
          {publicationHistory.slice(0, 20).map((event) => {
            const currentName = scopeNameById.get(event.scopeSetId) ?? event.scopeSetId;
            const previousName = event.previousScopeSetId ? scopeNameById.get(event.previousScopeSetId) ?? event.previousScopeSetId : null;
            return (
              <div key={event.eventId} className="list-row">
                <div>
                  <strong>
                    {event.eventType === 'changed' ? 'Changed' : 'Published'} {formatPublicationTarget(event)}
                  </strong>
                  <div className="muted">
                    {previousName ? `${previousName} v${event.previousVersion ?? '-'} -> ` : ''}{currentName} v{event.version}
                  </div>
                  <div className="muted">
                    {formatDate(event.changedAtUtc)}{event.changedBy ? ` by ${event.changedBy}` : ''}
                  </div>
                </div>
                <span className={event.eventType === 'changed' ? 'pill warning' : 'pill success'}>
                  {event.eventType}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div id="scopes-import-draft" className="card" style={{ marginTop: '12px' }}>
        <h3>{editingImportId ? 'Edit Import Draft' : 'Import Draft'}</h3>
        {editingImportId && (
          <div className="pill warning" style={{ marginBottom: '10px' }}>
            Editing draft {importName || editingImportId}
          </div>
        )}
        {m365GraphImportEnabled && <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Microsoft 365 import name</div>
            <input
              className="input"
              value={graphImportName}
              onChange={(event) => setGraphImportName(event.target.value)}
              placeholder="M365 organisation Scope import"
              disabled={graphImporting || saving}
            />
          </label>
          <div>
            <div className="muted" style={{ marginBottom: '4px' }}>Microsoft Graph organisation structure</div>
            <button
              className="btn"
              type="button"
              onClick={() => { void createGraphImportDraft(); }}
              disabled={graphImporting || saving || !selectedTenantId}
            >
              {graphImporting ? 'Importing...' : 'Import from Microsoft 365'}
            </button>
          </div>
        </div>}
        {m365GraphImportEnabled && <p className="muted">
          Optional feature: requires delegated User.Read.All and Organization.Read.All for this import only. It creates a complete draft from the tenant organisation, departments, and office locations; review it before publishing.
        </p>}
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Name</div>
            <input className="input" value={importName} onChange={(event) => setImportName(event.target.value)} disabled={saving} />
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Source</div>
            <select
              className="input"
              value={importSource}
              onChange={(event) => setImportSource(event.target.value as ScopeSource)}
              disabled={saving}
            >
              <option value="portal">Portal</option>
              <option value="spfx_export">SPFx export</option>
              <option value="m365_graph_structure">M365 org structure</option>
            </select>
          </label>
        </div>
        <div className="grid columns-2" style={{ marginTop: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Source instance id</div>
            <input className="input" value={sourceInstanceId} onChange={(event) => setSourceInstanceId(event.target.value)} disabled={saving} />
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Description</div>
            <input className="input" value={importDescription} onChange={(event) => setImportDescription(event.target.value)} disabled={saving} />
          </label>
        </div>
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
            <div>
              <strong>Draft entries</strong>
              <div className="muted">Edit the Scope tree without hand-editing JSON. Choose a parent Scope to place an entry beneath it.</div>
            </div>
            <button className="btn secondary" type="button" onClick={addDraftEntry} disabled={saving || Boolean(draftImportPayload.error)}>
              Add entry
            </button>
          </div>
          {draftImportPayload.error && <div className="pill warning">Payload JSON is not editable as rows: {draftImportPayload.error}</div>}
          {!draftImportPayload.error && draftImportPayload.payload?.entries.length === 0 && (
            <span className="badge-subtle">No draft entries.</span>
          )}
          {!draftImportPayload.error && draftImportPayload.payload?.entries.map((entry, index) => (
            <div key={`${entry.scopeKey || entry.localId || index}-${index}`} className="list-row" style={{ alignItems: 'stretch', marginTop: '10px' }}>
              <div style={{ display: 'grid', gap: '8px', width: '100%' }}>
                <div className="grid columns-2">
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Title</div>
                    <input
                      className="input"
                      value={entry.title}
                      onChange={(event) => updateDraftEntry(index, { title: event.target.value })}
                      disabled={saving}
                    />
                  </label>
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Scope key</div>
                    <input
                      className="input"
                      value={entry.scopeKey ?? ''}
                      onChange={(event) => updateDraftEntry(index, { scopeKey: event.target.value, localId: event.target.value })}
                      disabled={saving}
                    />
                  </label>
                </div>
                <div className="grid columns-2">
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Parent Scope</div>
                    <select
                      className="input"
                      value={entry.parentScopeKey ?? ''}
                      onChange={(event) => updateDraftEntry(index, {
                        parentScopeKey: event.target.value || null,
                        parentLocalId: event.target.value || null
                      })}
                      disabled={saving}
                    >
                      <option value="">Root</option>
                      {draftImportPayload.payload.entries
                        .filter((_candidate, candidateIndex) => candidateIndex !== index)
                        .map((candidate, candidateIndex) => {
                          const key = candidate.scopeKey || candidate.localId || `entry-${candidateIndex + 1}`;
                          return <option key={`${key}-${candidateIndex}`} value={key}>{candidate.title || key}</option>;
                        })}
                    </select>
                  </label>
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Path</div>
                    <input
                      className="input"
                      value={entry.path ?? ''}
                      onChange={(event) => updateDraftEntry(index, { path: event.target.value || null })}
                      disabled={saving}
                    />
                  </label>
                </div>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={entry.isActive !== false}
                    onChange={(event) => updateDraftEntry(index, { isActive: event.target.checked })}
                    disabled={saving}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div>
                <button className="btn secondary" type="button" onClick={() => removeDraftEntry(index)} disabled={saving}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
            <div>
              <strong>Draft stakeholders</strong>
              <div className="muted">Stakeholders can later be suggested when a matching Scope is added to an Audit.</div>
            </div>
            <button className="btn secondary" type="button" onClick={addDraftStakeholder} disabled={saving || Boolean(draftImportPayload.error)}>
              Add stakeholder
            </button>
          </div>
          {!draftImportPayload.error && (!draftImportPayload.payload?.stakeholders || draftImportPayload.payload.stakeholders.length === 0) && (
            <span className="badge-subtle">No draft stakeholders.</span>
          )}
          {!draftImportPayload.error && draftImportPayload.payload?.stakeholders?.map((stakeholder, index) => (
            <div key={`${stakeholder.stakeholderRef || index}-${index}`} className="list-row" style={{ alignItems: 'stretch', marginTop: '10px' }}>
              <div style={{ display: 'grid', gap: '8px', width: '100%' }}>
                <div className="grid columns-2">
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Stakeholder ref</div>
                    <input
                      className="input"
                      value={stakeholder.stakeholderRef}
                      onChange={(event) => updateDraftStakeholder(index, { stakeholderRef: event.target.value })}
                      disabled={saving}
                    />
                  </label>
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Display name</div>
                    <input
                      className="input"
                      value={stakeholder.displayName ?? ''}
                      onChange={(event) => updateDraftStakeholder(index, { displayName: event.target.value || null })}
                      disabled={saving}
                    />
                  </label>
                </div>
                <div className="grid columns-2">
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Type</div>
                    <select
                      className="input"
                      value={stakeholder.stakeholderType}
                      onChange={(event) => updateDraftStakeholder(index, {
                        stakeholderType: event.target.value as ScopeImportStakeholder['stakeholderType']
                      })}
                      disabled={saving}
                    >
                      <option value="email">Email</option>
                      <option value="portal_contact">Portal contact</option>
                      <option value="entra_group">Entra group</option>
                      <option value="sharepoint_group">SharePoint group</option>
                    </select>
                  </label>
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Applies to Scope</div>
                    <select
                      className="input"
                      value={stakeholder.scopeKey ?? ''}
                      onChange={(event) => updateDraftStakeholder(index, {
                        scopeKey: event.target.value || null,
                        localId: event.target.value || null
                      })}
                      disabled={saving}
                    >
                      <option value="">All entries</option>
                      {draftImportPayload.payload.entries.map((entry, entryIndex) => {
                        const key = entry.scopeKey || entry.localId || `entry-${entryIndex + 1}`;
                        return <option key={`${key}-${entryIndex}`} value={key}>{entry.title || key}</option>;
                      })}
                    </select>
                  </label>
                </div>
                <div className="grid columns-2">
                  <label>
                    <div className="muted" style={{ marginBottom: '4px' }}>Use as</div>
                    <select
                      className="input"
                      value={stakeholder.bindingMode ?? 'audit_stakeholder_suggestion'}
                      onChange={(event) => updateDraftStakeholder(index, {
                        bindingMode: event.target.value as ScopeImportStakeholder['bindingMode']
                      })}
                      disabled={saving}
                    >
                      <option value="audit_stakeholder_suggestion">Suggest for Audit stakeholders</option>
                      <option value="notification_default">Use for notifications</option>
                      <option value="assignment_default">Use for assignments</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '22px' }}>
                    <input
                      type="checkbox"
                      checked={stakeholder.inheritsToChildren !== false}
                      onChange={(event) => updateDraftStakeholder(index, { inheritsToChildren: event.target.checked })}
                      disabled={saving}
                    />
                    <span>Inherit to child Scopes</span>
                  </label>
                </div>
              </div>
              <div>
                <button className="btn secondary" type="button" onClick={() => removeDraftStakeholder(index)} disabled={saving}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <label style={{ display: 'block', marginTop: '10px' }}>
          <div className="muted" style={{ marginBottom: '4px' }}>Payload JSON</div>
          <textarea
            className="input"
            rows={10}
            value={importJson}
            onChange={(event) => setImportJson(event.target.value)}
            disabled={saving}
          />
        </label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn" type="button" onClick={() => { void createDraftImport(); }} disabled={saving || !importName.trim()}>
            {editingImportId ? 'Update draft' : 'Create draft'}
          </button>
          {editingImportId && (
            <button className="btn secondary" type="button" onClick={cancelImportEditing} disabled={saving}>
              Cancel edit
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Imports</h3>
        {imports.some(importHasWarnings) && (
          <div className="pill warning" style={{ marginTop: '8px' }}>
            Some imports need review before they can be published. Acknowledge the warning on each import after checking the entries.
          </div>
        )}
        {imports.length === 0 && <span className="badge-subtle">No imports.</span>}
        <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
          {imports.map((record) => {
            const warnings = importWarningText(record);
            const warningsAcknowledged = acknowledgedWarningImports[record.scopeImportId] === true;
            const publishDisabled = saving || (warnings.length > 0 && !warningsAcknowledged);
            return (
              <div key={record.scopeImportId} className="list-row">
                <div>
                  <strong>{record.proposedName}</strong>
                  <div className="muted">
                    {formatSource(record.importSource)} · {record.status} · {formatDate(record.createdAtUtc)}
                  </div>
                  {record.description && <div className="muted">{record.description}</div>}
                  {record.payload?.metadata?.revisionOfScopeKey && (
                    <div className="muted">
                      Revision of {record.payload.metadata.revisionOfScopeKey} v{record.payload.metadata.revisionOfVersion ?? '-'}
                    </div>
                  )}
                  {warnings.length > 0 && (
                    <div className="pill warning">
                      {warnings.join(' ')} Review before publishing.
                    </div>
                  )}
                  {warnings.length > 0 && record.status === 'draft' && (
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                      <input
                        type="checkbox"
                        checked={warningsAcknowledged}
                        onChange={(event) => setAcknowledgedWarningImports((previous) => ({
                          ...previous,
                          [record.scopeImportId]: event.target.checked
                        }))}
                        disabled={saving}
                      />
                      <span>I have reviewed these import warnings</span>
                    </label>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {record.status === 'draft' && (
                    <>
                      <button className="btn secondary" type="button" onClick={() => loadImportForEditing(record)} disabled={saving}>
                        Edit draft
                      </button>
                      <button className="btn secondary" type="button" onClick={() => { void publishImport(record, false); }} disabled={publishDisabled}>
                        Publish
                      </button>
                      <button className="btn" type="button" onClick={() => { void publishImport(record, true); }} disabled={publishDisabled}>
                        Publish default
                      </button>
                    </>
                  )}
                  {record.publishedScopeSetId && <span className="pill success">published</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Installation status</h3>
        {activeInstances.length === 0 && <span className="badge-subtle">No active installations registered yet.</span>}
        <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
          {activeInstances.map((instance) => {
            const status = statusByInstanceId.get(instance.instanceId);
            const publication = instancePublicationByInstanceId.get(instance.instanceId);
            const appliedScopeName = status?.appliedScopeSetId
              ? scopeNameById.get(status.appliedScopeSetId) ?? status.appliedScopeSetId
              : null;
            return (
              <div key={instance.instanceId} className="list-row">
                <div>
                  <strong>{instance.siteUrl || instance.pageUrl || instance.instanceId}</strong>
                  <div className="muted">
                    {instance.status ?? 'unknown'}{instance.webPartVersion ? ` · SPFx v${instance.webPartVersion}` : ''}
                    {instance.lastSeenAt ? ` · last seen ${formatDate(instance.lastSeenAt)}` : ''}
                  </div>
                  <div className="muted">
                    Assigned Scope: {publication ? `${publication.scope.name} v${publication.scope.version}` : 'tenant default'}
                  </div>
                  {status ? (
                    <div className="muted">
                      Reported: {formatMode(status.scopeMode)} · {status.syncStatus} · {formatDate(status.lastReportedAtUtc)}
                    </div>
                  ) : (
                    <div className="muted">Reported: no SPFx Scope report yet</div>
                  )}
                  {appliedScopeName && (
                    <div className="muted">Applied Scope: {appliedScopeName} v{status?.appliedVersion ?? '-'}</div>
                  )}
                  {status?.lastError && <div className="pill warning">{status.lastError}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && <span className="badge-subtle">Loading Scopes...</span>}
      {error && <span className="pill warning">{error}</span>}
      {savedMessage && <span className="pill success">{savedMessage}</span>}
    </div>
  );
};

export default ScopesAdmin;


